import { useState, useEffect, useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Compass, 
  TrendingUp, 
  Rocket, 
  Calendar, 
  Edit2, 
  Trash2, 
  Loader2,
  Search,
  CheckCircle,
  Building2,
  Expand,
  AlertTriangle,
  Star,
  History,
  Target,
  Check,
  MessageSquare
} from 'lucide-react'
import { 
  CareerAspiration, 
  CreateCareerAspirationRequest, 
  CareerObjectiveType,
  IDPItemStatus 
} from '@/services/api/idpService'
import type { JobDefinition } from '@/services/api/integrationService'
import roleMasterService, { type RoleTitle } from '@/services/api/roleMasterService'
import { ScrollArea } from '@/components/ui/scroll-area'
import toast from 'react-hot-toast'
import { sastToday } from '@/components/admin/lifecycle/spellCoverage'

// #23/#3: geographic-mobility options live on Career Aspirations now (moved
// out of the Coaching & Impact reflections panel). Wording matches the HR
// brief — soft, no-judgment.
const MOBILITY_CHOICE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'maybe_future', label: 'Not at this time, potentially in future' },
  { value: 'no', label: 'No' },
] as const
type MobilityChoice = (typeof MOBILITY_CHOICE_OPTIONS)[number]['value']

// #3: Eduvos's physical campuses, used by the geographic-mobility picker.
// Static (and team-member accessible) on purpose — the previous HR-Ops
// employee-search facets endpoint is admin-scoped and 401s for a team member,
// hanging the picker on "Loading campuses…". Mirrors the canonical
// `closest_campuses` list in integration-service reference_data.py; update
// both together if Eduvos opens/closes a campus.
const EDUVOS_CAMPUSES: string[] = [
  'Bedfordview',
  'Bloemfontein',
  'Cape Town - Mowbray',
  'Cape Town - Parc du Cap',
  'Cape Town - Tygervalley',
  'Durban',
  'East London',
  'Mbombela',
  'Midrand',
  'Nelson Mandela Bay',
  'Potchefstroom',
  'Pretoria',
  'Vanderbijlpark',
  // Not a campus: the online delivery, which the HR process owner asked for as a mobility
  // option (7 Sep 2026). Kept out of the integration-service campus list.
  'Online',
]

/** "Future roles that intrigue me" is stored as one text field; the picker
 *  keeps it as titles separated by "; " so older free text still reads. */
const splitRoles = (text?: string | null): string[] =>
  (text || '')
    .split(/;\s*|\n/)
    .map((t) => t.trim())
    .filter(Boolean)

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Performance assessment record
export interface PerformanceAssessment {
  year: number
  period: string // e.g., "Mid-Year", "Year-End"
  rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak'
  date: string
}

interface CareerGoalSectionProps {
  careerAspiration: CareerAspiration | null
  onSave: (data: CreateCareerAspirationRequest) => Promise<void>
  onUpdate: (aspirationId: string, data: CreateCareerAspirationRequest) => Promise<void>
  onDelete: (aspirationId: string) => Promise<void>
  onManagerAction?: (aspirationId: string, title: string) => void
  isFinalized: boolean
  userRole: 'employee' | 'manager'
  // Performance history for context
  performanceHistory?: PerformanceAssessment[]
}

const TIMELINE_OPTIONS = [
  { value: '6_months', label: '6 months' },
  { value: '1_year', label: '1 year' },
  { value: '2_years', label: '2 years' },
  { value: '3_years', label: '3 years' },
  { value: '5_years', label: '5+ years' },
]

// Career objective type configurations
const CAREER_OBJECTIVE_OPTIONS = {
  CURRENT_ROLE: {
    title: 'Current Role',
    subtitle: 'Master my current role',
    description: '"Am I doing everything I can to master my current role?" Focus on core skills and proficiency.',
    icon: TrendingUp,
    color: 'blue',
    skills: ['Relationship building', 'Master processes', 'Networking', 'Ability to influence others']
  },
  EXPAND_ROLE: {
    title: 'Expand Impact', 
    subtitle: 'Increase my impact',
    description: '"How can I expand my role and increase my impact on a broader scale?" Expert in current role.',
    icon: Expand,
    color: 'teal',
    skills: ['Projects outside of your role', 'Joining committees', 'Gaining exposure']
  },
  NEXT_ROLE: {
    title: 'Another Role',
    subtitle: 'Advance to a new position',
    description: '"What are next-role options for me?" Building skills and gaining exposure through roles.',
    icon: Rocket,
    color: 'purple',
    skills: ['Expand breadth and depth of skills', 'Build capabilities for target role']
  }
}

const RATING_CONFIG = {
  exceptional: { label: 'Exceptional', color: 'text-green-700 bg-green-100', icon: Star },
  full: { label: 'Proficient', color: 'text-blue-700 bg-blue-100', icon: CheckCircle },
  'not-yet-full': { label: 'Developing', color: 'text-amber-700 bg-amber-100', icon: TrendingUp },
  weak: { label: 'Needs Improvement', color: 'text-red-700 bg-red-100', icon: AlertTriangle }
}

// Map legacy types to new types
const mapLegacyType = (type: string | undefined): CareerObjectiveType => {
  if (type === 'CURRENT_ROLE') return 'CURRENT_ROLE'
  if (type === 'NEXT_ROLE') return 'NEXT_ROLE'
  if (type === 'CURRENT_ROLE' || type === 'EXPAND_ROLE' || type === 'NEXT_ROLE') return type as CareerObjectiveType
  return 'CURRENT_ROLE'
}

/**
 * A next-role goal's target date: today plus the timeframe, as a South
 * African day. (toISOString() gave the UTC day, which saved a date a day
 * early between 00:00 and 02:00.)
 */
export function calculateTargetDate(timeframe: string): string {
  const now = new Date()
  switch (timeframe) {
    case '6_months':
      now.setMonth(now.getMonth() + 6)
      break
    case '1_year':
      now.setFullYear(now.getFullYear() + 1)
      break
    case '2_years':
      now.setFullYear(now.getFullYear() + 2)
      break
    case '3_years':
      now.setFullYear(now.getFullYear() + 3)
      break
    case '5_years':
      now.setFullYear(now.getFullYear() + 5)
      break
  }
  return sastToday(now)
}

export function CareerGoalSection({
  careerAspiration,
  onSave,
  onUpdate,
  onDelete,
  onManagerAction,
  isFinalized,
  userRole,
  performanceHistory = []
}: CareerGoalSectionProps) {
  const [isEditing, setIsEditing] = useState(!careerAspiration)
  const [isSaving, setIsSaving] = useState(false)
  const [objectiveType, setObjectiveType] = useState<CareerObjectiveType>(
    mapLegacyType(careerAspiration?.aspiration_type)
  )
  const [timeline, setTimeline] = useState(careerAspiration?.timeframe || '2_years')
  const [employeeNotes, setEmployeeNotes] = useState(careerAspiration?.description || '')
  const [managerNotes, setManagerNotes] = useState((careerAspiration as any)?.manager_notes || '')

  // #4: Longer-term Professional Aspirations (HR slide 8).
  const [futureRoles, setFutureRoles] = useState<string[]>(splitRoles(careerAspiration?.future_roles_text))
  const [futureRoleSearch, setFutureRoleSearch] = useState('')
  // The current Role Master titles: one load, filtered locally, shared by the
  // target-role picker and the future-roles picker.
  const [roleTitles, setRoleTitles] = useState<RoleTitle[] | null>(null)
  const [futureSkillsText, setFutureSkillsText] = useState(careerAspiration?.future_skills_text || '')

  // #23/#3: geographic mobility + preferred campuses.
  const deriveMobilityChoice = (a: CareerAspiration | null): MobilityChoice | '' => {
    if (!a) return ''
    // Existing rows only stored a boolean. Map true -> 'yes', and leave the
    // softer "maybe_future" / explicit "no" to be re-selected by the user.
    return a.geographic_mobility ? 'yes' : ''
  }
  const [mobilityChoice, setMobilityChoice] = useState<MobilityChoice | ''>(
    deriveMobilityChoice(careerAspiration),
  )
  const [preferredCampuses, setPreferredCampuses] = useState<string[]>(
    careerAspiration?.preferred_campuses || [],
  )
  // #3: the campus picker lists Eduvos's physical campuses. Previously sourced
  // from the HR-Ops employee-search facets endpoint, which is admin-scoped —
  // for a team member (or a demo persona) it 401s and the picker hung on
  // "Loading campuses…" forever. The campus set is small and fixed, so use the
  // canonical static list (mirrors reference_data.py `closest_campuses`).
  const campusOptions = EDUVOS_CAMPUSES
  
  // Job search state (for NEXT_ROLE target)
  const [jobs, setJobs] = useState<JobDefinition[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobSearch, setJobSearch] = useState('')
  const [selectedJob, setSelectedJob] = useState<JobDefinition | null>(null)

  // Debounce search query (target-role picker for NEXT_ROLE)
  const debouncedSearch = useDebounce(jobSearch, 300)

  // Calculate performance recommendation
  const performanceRecommendation = useMemo(() => {
    if (performanceHistory.length === 0) return null
    
    const recentRatings = performanceHistory.slice(0, 6)
    const hasNotFull = recentRatings.some(r => r.rating === 'not-yet-full' || r.rating === 'weak')
    const allExceptional = recentRatings.every(r => r.rating === 'exceptional')
    const mostlyFull = recentRatings.filter(r => r.rating === 'full' || r.rating === 'exceptional').length >= recentRatings.length * 0.8
    
    if (hasNotFull) {
      return {
        recommended: 'CURRENT_ROLE' as CareerObjectiveType,
        message: 'Based on recent impact & growth, focusing on mastering your current role is recommended. Research shows today\'s skills have 5x greater impact on sustained impact & growth.',
        severity: 'warning'
      }
    }
    if (allExceptional) {
      return {
        recommended: 'NEXT_ROLE' as CareerObjectiveType,
        message: 'Your exceptional impact & growth track record suggests you may be ready to explore next-role opportunities.',
        severity: 'success'
      }
    }
    if (mostlyFull) {
      return {
        recommended: 'EXPAND_ROLE' as CareerObjectiveType,
        message: 'Your consistent proficient impact & growth indicates readiness to expand your impact in your current role.',
        severity: 'info'
      }
    }
    return null
  }, [performanceHistory])

  // Load jobs when switching to NEXT_ROLE type or search changes
  useEffect(() => {
    if (objectiveType === 'NEXT_ROLE' && !selectedJob) {
      loadJobs(debouncedSearch || undefined)
    }
  }, [objectiveType, debouncedSearch, selectedJob])

  // Initialize selected job from existing aspiration
  useEffect(() => {
    if (careerAspiration?.aspiration_type === 'NEXT_ROLE') {
      setSelectedJob({
        job_id: (careerAspiration as any).target_position_id || '',
        title_id: careerAspiration.title,
        description: (careerAspiration as any).target_position_title || careerAspiration.title
      } as JobDefinition)
    }
    // #4/#3: re-init the new fields from the loaded aspiration.
    setFutureRoles(splitRoles(careerAspiration?.future_roles_text))
    setFutureSkillsText(careerAspiration?.future_skills_text || '')
    setMobilityChoice(deriveMobilityChoice(careerAspiration ?? null))
    setPreferredCampuses(careerAspiration?.preferred_campuses || [])
  }, [careerAspiration])

  const toggleCampus = (campus: string) => {
    setPreferredCampuses((prev) =>
      prev.includes(campus) ? prev.filter((c) => c !== campus) : [...prev, campus],
    )
  }

  const ensureRoleTitles = async (): Promise<RoleTitle[]> => {
    if (roleTitles) return roleTitles
    const titles = await roleMasterService.listTitles()
    setRoleTitles(titles)
    return titles
  }

  // The target-role picker keeps the job shape it always had; the rows now
  // come from the Role Master, titled as it names the roles today.
  const toJob = (t: RoleTitle): JobDefinition =>
    ({ job_id: t.fo_job_id || t.role_code, title_id: t.title, job_family_id: t.job_family || undefined, description: t.title }) as JobDefinition

  const loadJobs = async (search?: string) => {
    setJobsLoading(true)
    try {
      const titles = await ensureRoleTitles()
      const q = (search || '').trim().toLowerCase()
      const hits = q
        ? titles.filter((t) => t.title.toLowerCase().includes(q) || (t.job_family || '').toLowerCase().includes(q))
        : titles
      setJobs(hits.slice(0, 50).map(toJob))
    } catch (error) {
      console.error('Failed to load role titles:', error)
      toast.error('Could not load the role titles')
    } finally {
      setJobsLoading(false)
    }
  }

  const futureRoleMatches = useMemo(() => {
    const q = futureRoleSearch.trim().toLowerCase()
    if (!q || !roleTitles) return []
    return roleTitles
      .filter((t) => !futureRoles.includes(t.title))
      .filter((t) => t.title.toLowerCase().includes(q) || (t.job_family || '').toLowerCase().includes(q))
      .slice(0, 20)
  }, [futureRoleSearch, roleTitles, futureRoles])

  const handleSave = async () => {
    // Validate
    if (objectiveType === 'NEXT_ROLE' && !selectedJob) {
      toast.error('Please select a target role')
      return
    }

    setIsSaving(true)
    try {
      // Generate title based on objective type
      const getTitle = () => {
        switch (objectiveType) {
          case 'CURRENT_ROLE':
            return 'Master and grow in current role'
          case 'EXPAND_ROLE':
            return 'Expand impact and influence in current role'
          case 'NEXT_ROLE':
            return selectedJob?.title_id || selectedJob?.job_id || 'Advance to new role'
          default:
            return 'Career Aspirations'
        }
      }

      const data: CreateCareerAspirationRequest & { manager_notes?: string } = {
        aspiration_type: objectiveType,
        title: getTitle(),
        description: employeeNotes,
        timeframe: objectiveType === 'NEXT_ROLE' ? timeline : undefined,
        target_date: objectiveType === 'NEXT_ROLE' ? calculateTargetDate(timeline) : undefined,
        function_mobility: objectiveType === 'NEXT_ROLE',
        role_mobility: objectiveType === 'NEXT_ROLE' || objectiveType === 'EXPAND_ROLE',
        // #23/#3: geographic mobility now driven by the explicit 3-option
        // choice. Only "yes" carries preferred campuses.
        geographic_mobility: mobilityChoice === 'yes',
        preferred_campuses: mobilityChoice === 'yes' ? preferredCampuses : [],
        // #4: Longer-term Professional Aspirations.
        future_roles_text: futureRoles.join('; ') || undefined,
        future_skills_text: futureSkillsText || undefined,
        employee_notes: employeeNotes,
        item_status: userRole === 'manager' ? IDPItemStatus.APPROVED : IDPItemStatus.EMPLOYEE_PROPOSED,
        target_position_id: objectiveType === 'NEXT_ROLE' ? selectedJob?.job_id : undefined,
        target_position_title: objectiveType === 'NEXT_ROLE' ? selectedJob?.title_id : undefined,
        ...(userRole === 'manager' && managerNotes ? { manager_notes: managerNotes } : {}),
      }

      if (careerAspiration) {
        await onUpdate(careerAspiration.id, data)
        toast.success('Career objective updated')
      } else {
        await onSave(data)
        toast.success('Career objective saved')
      }
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save career objective:', error)
      toast.error('Failed to save career objective')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!careerAspiration) return
    
    if (!confirm('Are you sure you want to remove your career goal?')) return
    
    try {
      await onDelete(careerAspiration.id)
      toast.success('Career goal removed')
      setIsEditing(true)
      setObjectiveType('CURRENT_ROLE')
      setEmployeeNotes('')
      setManagerNotes('')
      setSelectedJob(null)
    } catch (error) {
      console.error('Failed to delete career goal:', error)
      toast.error('Failed to remove career goal')
    }
  }

  const getTimelineLabel = (value: string) => 
    TIMELINE_OPTIONS.find(t => t.value === value)?.label || value

  // Get display info for objective type
  const getObjectiveDisplayInfo = (type: string) => {
    const mappedType = mapLegacyType(type)
    switch (mappedType) {
      case 'CURRENT_ROLE':
        return { title: 'Current Role', subtitle: 'Master my current role', icon: TrendingUp, color: 'blue' }
      case 'EXPAND_ROLE':
        return { title: 'Expand Impact', subtitle: 'Increase my impact', icon: Expand, color: 'teal' }
      case 'NEXT_ROLE':
        return { title: 'Another Role', subtitle: 'Advance to new position', icon: Rocket, color: 'purple' }
      default:
        return { title: 'Career Aspirations', subtitle: '', icon: Compass, color: 'gray' }
    }
  }

  // Display mode (when not editing)
  if (!isEditing && careerAspiration) {
    const displayInfo = getObjectiveDisplayInfo(careerAspiration.aspiration_type)
    const IconComponent = displayInfo.icon
    const colorClasses = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-50 border-blue-200 text-blue-700' },
      teal: { bg: 'bg-teal-100', text: 'text-teal-600', badge: 'bg-teal-50 border-teal-200 text-teal-700' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', badge: 'bg-purple-50 border-purple-200 text-purple-700' },
      gray: { bg: 'bg-gray-100', text: 'text-gray-600', badge: 'bg-gray-50 border-gray-200 text-gray-700' }
    }
    const colors = colorClasses[displayInfo.color as keyof typeof colorClasses]

    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full ${colors.bg} flex items-center justify-center`}>
              <IconComponent className={`w-6 h-6 ${colors.text}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{displayInfo.title}</h3>
              <p className="text-sm text-gray-500">{displayInfo.subtitle}</p>
            </div>
          </div>
          
          {/* The team member always owns their own Growth Focus — editable
              even after the People Leader has acknowledged or the plan was
              finalized. Saving resets the item to "Pending People Leader
              Review" and the backend re-opens the plan. */}
          {userRole === 'employee' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Manager Actions */}
          {!isFinalized && userRole === 'manager' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              {careerAspiration.item_status === 'EMPLOYEE_PROPOSED' && onManagerAction && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onManagerAction(careerAspiration.id, careerAspiration.title)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Acknowledge
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Timeline - only for NEXT_ROLE */}
          {careerAspiration.timeframe && mapLegacyType(careerAspiration.aspiration_type) === 'NEXT_ROLE' && (
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>Timeline: <span className="font-medium">{getTimelineLabel(careerAspiration.timeframe)}</span></span>
            </div>
          )}

          {/* Target Role for NEXT_ROLE */}
          {mapLegacyType(careerAspiration.aspiration_type) === 'NEXT_ROLE' && (
            <div className="flex items-center gap-2 text-gray-600">
              <Rocket className="w-4 h-4 text-purple-600" />
              <span>Target Role: <span className="font-medium text-purple-700">{careerAspiration.title}</span></span>
            </div>
          )}

          {/* #4: Longer-term Professional Aspirations */}
          {careerAspiration.future_roles_text && (
            <div className="bg-indigo-50/60 rounded-lg p-4 border border-indigo-100">
              <p className="text-sm font-medium text-indigo-800 mb-1 flex items-center gap-1">
                <Target className="w-3 h-3" />
                Future roles that intrigue me
              </p>
              <div className="flex flex-wrap gap-1.5">
                {splitRoles(careerAspiration.future_roles_text).map((t) => (
                  <span key={t} className="rounded-full border border-indigo-200 bg-white px-2.5 py-0.5 text-sm text-indigo-900">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {careerAspiration.future_skills_text && (
            <div className="bg-indigo-50/60 rounded-lg p-4 border border-indigo-100">
              <p className="text-sm font-medium text-indigo-800 mb-1 flex items-center gap-1">
                <Rocket className="w-3 h-3" />
                Future skills I need or desire
              </p>
              <p className="text-gray-700 whitespace-pre-wrap">{careerAspiration.future_skills_text}</p>
            </div>
          )}

          {/* #23/#3: Geographic Mobility */}
          {careerAspiration.geographic_mobility && (
            <div className="bg-amber-50/60 rounded-lg p-4 border border-amber-100">
              <p className="text-sm font-medium text-amber-800 mb-1 flex items-center gap-1">
                <Compass className="w-3 h-3" />
                Open to geographic mobility
              </p>
              {careerAspiration.preferred_campuses && careerAspiration.preferred_campuses.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {careerAspiration.preferred_campuses.map((campus) => (
                    <Badge key={campus} variant="outline" className="bg-white border-amber-200 text-amber-800">
                      {campus}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-amber-700">No specific campuses noted.</p>
              )}
            </div>
          )}

          {/* Description/Notes */}
          {careerAspiration.description && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Team Member Notes</p>
              <p className="text-gray-600">{careerAspiration.description}</p>
            </div>
          )}

          {/* Manager Notes */}
          {(careerAspiration as any).manager_notes && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <p className="text-sm font-medium text-green-800 mb-1 flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                People Leader Feedback
              </p>
              <p className="text-green-700">{(careerAspiration as any).manager_notes}</p>
            </div>
          )}

          {/* Status Badge */}
          <div className="pt-2">
            <Badge 
              variant="outline" 
              className={
                careerAspiration.item_status === 'APPROVED' 
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-amber-50 border-amber-200 text-amber-700'
              }
            >
              {careerAspiration.item_status === 'APPROVED' ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Acknowledged by People Leader
                </>
              ) : (
                'Pending People Leader Review'
              )}
            </Badge>
          </div>
        </div>
      </Card>
    )
  }

  // Edit/Create mode. Heading removed — the "Growth Focus" sub-tab already
  // names this surface, so the in-card "Set Your Career Aspirations" title +
  // subtitle were redundant.
  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* #11: People Leader is read-only on the member's own aspirations */}
        {userRole === 'manager' && (
          <div className="p-3 rounded-lg text-sm bg-blue-50 text-blue-700 border border-blue-200">
            These are your team member's own aspirations — they are read-only for you. Add your guidance in People Leader Notes below.
          </div>
        )}

        {/* #11: member-owned fields are natively disabled for the People Leader.
            The People Leader Notes block + Save/Cancel actions live OUTSIDE this
            fieldset so the PL can still add notes and save. */}
        <fieldset disabled={userRole === 'manager'} className="space-y-6 border-0 p-0 m-0 min-w-0">
        {/* Performance History Context */}
        {performanceHistory.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Recent Impact & Growth History</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {performanceHistory.slice(0, 6).map((assessment, idx) => {
                const config = RATING_CONFIG[assessment.rating]
                const Icon = config.icon
                return (
                  <div key={idx} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${config.color}`}>
                    <Icon className="w-3 h-3" />
                    <span>{assessment.period} {assessment.year}</span>
                  </div>
                )
              })}
            </div>
            {performanceRecommendation && (
              <div className={`text-xs p-2 rounded ${
                performanceRecommendation.severity === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                performanceRecommendation.severity === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                <strong>Recommendation:</strong> {performanceRecommendation.message}
              </div>
            )}
          </div>
        )}

        {/* Current Growth Focus - 3 Options (#4 relabel; options unchanged) */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Current Growth Focus</Label>
          <RadioGroup 
            value={objectiveType} 
            onValueChange={(value) => setObjectiveType(value as CareerObjectiveType)}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {/* Current Role */}
            <div>
              <RadioGroupItem value="CURRENT_ROLE" id="current" className="peer sr-only" />
              <Label 
                htmlFor="current" 
                className="flex flex-col items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:border-blue-300 h-full"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-blue-900">Current Role</p>
                  <p className="text-xs text-gray-500 mt-1">Master my current role</p>
                </div>
              </Label>
            </div>
            
            {/* Expand Impact */}
            <div>
              <RadioGroupItem value="EXPAND_ROLE" id="expand" className="peer sr-only" />
              <Label 
                htmlFor="expand" 
                className="flex flex-col items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all peer-data-[state=checked]:border-teal-500 peer-data-[state=checked]:bg-teal-50 hover:border-teal-300 h-full"
              >
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <Expand className="w-6 h-6 text-teal-600" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-teal-900">Expand Impact</p>
                  <p className="text-xs text-gray-500 mt-1">Increase my impact</p>
                </div>
              </Label>
            </div>
            
            {/* Another Role */}
            <div>
              <RadioGroupItem value="NEXT_ROLE" id="next" className="peer sr-only" />
              <Label 
                htmlFor="next" 
                className="flex flex-col items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all peer-data-[state=checked]:border-purple-500 peer-data-[state=checked]:bg-purple-50 hover:border-purple-300 h-full"
              >
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-purple-900">Another Role</p>
                  <p className="text-xs text-gray-500 mt-1">Advance to new position</p>
                </div>
              </Label>
            </div>
          </RadioGroup>
          
          {/* Objective Type Description */}
          <div className={`p-3 rounded-lg text-sm ${
            objectiveType === 'CURRENT_ROLE' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
            objectiveType === 'EXPAND_ROLE' ? 'bg-teal-50 text-teal-700 border border-teal-200' :
            'bg-purple-50 text-purple-700 border border-purple-200'
          }`}>
            {CAREER_OBJECTIVE_OPTIONS[objectiveType].description}
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="text-xs font-medium">Focus on:</span>
              {CAREER_OBJECTIVE_OPTIONS[objectiveType].skills.map((skill, idx) => (
                <span key={idx} className="text-xs px-1.5 py-0.5 bg-white/50 rounded">{skill}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Target Role Selection (for NEXT_ROLE only) */}
        {objectiveType === 'NEXT_ROLE' && (
          <div className="space-y-2">
            <Label className="text-base font-medium">Select Target Role</Label>
            
            {selectedJob && (
              <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Rocket className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-purple-900">{selectedJob.title_id || selectedJob.job_id}</p>
                      {selectedJob.job_family_id && (
                        <p className="text-sm text-purple-600 mt-0.5">{selectedJob.job_family_id}</p>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setSelectedJob(null); setJobSearch('') }}
                    className="text-purple-600 hover:text-purple-800 border-purple-200"
                  >
                    Change
                  </Button>
                </div>
              </div>
            )}
            
            {!selectedJob && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search roles by title…"
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    className="pl-10"
                  />
                  {jobsLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />}
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  {jobsLoading && jobs.length === 0 ? (
                    <div className="p-6 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-500" />
                      <p className="text-sm text-gray-500">Loading roles…</p>
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="p-6 text-center">
                      <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">{jobSearch ? `No roles found for "${jobSearch}"` : 'No roles to show.'}</p>
                    </div>
                  ) : (
                    <>
                      <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500">{jobs.length} role{jobs.length !== 1 ? 's' : ''}</div>
                      <ScrollArea className="h-[200px]">
                        <div className="divide-y">
                          {jobs.map((job) => (
                            <button key={job.job_id} type="button" onClick={() => setSelectedJob(job)} className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors group">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 group-hover:text-purple-700 truncate">{job.title_id || job.job_id}</p>
                                  {job.job_family_id && <p className="text-xs text-purple-600 mt-0.5">{job.job_family_id}</p>}
                                </div>
                                <Rocket className="w-4 h-4 text-gray-300 group-hover:text-purple-500 flex-shrink-0 mt-1" />
                              </div>
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Timeline - Only for NEXT_ROLE */}
        {objectiveType === 'NEXT_ROLE' && (
          <div className="space-y-2">
            <Label className="text-base font-medium">Target Timeline</Label>
            <Select value={timeline} onValueChange={setTimeline}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMELINE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* #4: Longer-term Professional Aspirations (HR slide 8). Replaces the
            old single "Long-term Career Aspirations at Eduvos" job picker with
            two free-text reflections. Shown for all focus types. */}
        <div className="space-y-4 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">
          <Label className="text-base font-medium flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-600" />
            Longer-term Professional Aspirations
          </Label>

          <div className="space-y-2">
            <Label htmlFor="future-roles" className="text-sm font-medium text-gray-700">
              Future roles that intrigue me
            </Label>
            {futureRoles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {futureRoles.map((title) => (
                  <span
                    key={title}
                    className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-100 px-3 py-1 text-sm text-indigo-900"
                  >
                    {title}
                    <button
                      type="button"
                      onClick={() => setFutureRoles((prev) => prev.filter((t) => t !== title))}
                      aria-label={`Remove ${title}`}
                      className="text-indigo-500 hover:text-indigo-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="future-roles"
                placeholder="Add a role: start typing its title…"
                value={futureRoleSearch}
                onChange={(e) => setFutureRoleSearch(e.target.value)}
                onFocus={() => {
                  void ensureRoleTitles().catch(() => toast.error('Could not load the role titles'))
                }}
                className="pl-10"
                autoComplete="off"
              />
            </div>
            {futureRoleSearch.trim() && (
              <div className="max-h-48 divide-y overflow-y-auto rounded-lg border">
                {!roleTitles ? (
                  <p className="p-3 text-sm text-gray-500">Loading roles…</p>
                ) : futureRoleMatches.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">No roles found for "{futureRoleSearch}"</p>
                ) : (
                  futureRoleMatches.map((t) => (
                    <button
                      key={t.role_code}
                      type="button"
                      onClick={() => {
                        setFutureRoles((prev) => (prev.includes(t.title) ? prev : [...prev, t.title]))
                        setFutureRoleSearch('')
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50"
                    >
                      {t.title}
                      {t.job_family ? <span className="text-xs text-gray-500"> · {t.job_family}</span> : null}
                    </button>
                  ))
                )}
              </div>
            )}
            <p className="text-xs text-gray-500">Pick from the current role titles. The roles you choose are saved to your plan.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="future-skills" className="text-sm font-medium text-gray-700">
              Future skills I need or desire
            </Label>
            <Textarea
              id="future-skills"
              value={futureSkillsText}
              onChange={(e) => setFutureSkillsText(e.target.value)}
              placeholder="Skills or capabilities you'd love to build for where you're heading…"
              rows={3}
            />
          </div>
        </div>

        {/* #23/#3: Geographic Mobility — moved here from the Coaching & Impact
            panel. Soft, no-judgment framing. Selecting "Yes" reveals a campus
            multi-select sourced from org reference data. */}
        <div className="space-y-3 rounded-lg border border-amber-100 bg-amber-50/40 p-4">
          <Label className="text-base font-medium flex items-center gap-2">
            <Compass className="w-4 h-4 text-amber-600" />
            Geographic Mobility
          </Label>
          <p className="text-xs text-gray-500">
            Life is full of complicated priorities — no judgment. We're just keen to learn if you
            have interest and flexibility in other campuses for the right move.
          </p>
          <RadioGroup
            value={mobilityChoice}
            onValueChange={(value) => setMobilityChoice(value as MobilityChoice)}
            className="space-y-2"
          >
            {MOBILITY_CHOICE_OPTIONS.map((opt) => (
              <Label
                key={opt.value}
                htmlFor={`mobility-${opt.value}`}
                className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  mobilityChoice === opt.value
                    ? 'border-amber-400 bg-amber-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <RadioGroupItem value={opt.value} id={`mobility-${opt.value}`} />
                <span className="text-sm text-gray-800 font-normal">{opt.label}</span>
              </Label>
            ))}
          </RadioGroup>

          {mobilityChoice === 'yes' && (
            <div className="space-y-2 pt-1">
              <Label className="text-sm font-medium text-gray-700">
                Campuses I'd consider, or Online
              </Label>
              {campusOptions.length === 0 ? (
                <p className="text-sm text-gray-500">No campuses available.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {campusOptions.map((campus) => {
                    const active = preferredCampuses.includes(campus)
                    return (
                      <button
                        key={campus}
                        type="button"
                        onClick={() => toggleCampus(campus)}
                        aria-pressed={active}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          active
                            ? 'border-amber-400 bg-amber-100 text-amber-900'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5" />}
                        {campus}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        </fieldset>

        {/* Notes - Role specific (OUTSIDE the read-only fieldset) */}
        {userRole === 'employee' ? (
          <div className="space-y-2">
            <Label className="text-base font-medium">My Notes</Label>
            <Textarea
              value={employeeNotes}
              onChange={(e) => setEmployeeNotes(e.target.value)}
              placeholder={
                objectiveType === 'CURRENT_ROLE'
                  ? "What skills will help you elevate your current role?"
                  : objectiveType === 'EXPAND_ROLE'
                  ? "How can you increase your impact on a broader scale?"
                  : "Describe your motivation and readiness for this new role..."
              }
              rows={3}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Show Employee Notes as read-only for manager */}
            {employeeNotes && (
              <div className="space-y-2">
                <Label className="text-base font-medium text-gray-600">Team Member Notes</Label>
                <div className="p-3 bg-gray-50 rounded-lg border text-gray-700 text-sm">
                  {employeeNotes}
                </div>
              </div>
            )}
            
            {/* Manager's own notes */}
            <div className="space-y-2">
              <Label className="text-base font-medium">People Leader Notes</Label>
              <Textarea
                value={managerNotes}
                onChange={(e) => setManagerNotes(e.target.value)}
                placeholder="Add your feedback, guidance, or suggestions for this career objective..."
                rows={3}
                className="border-green-200 focus:border-green-400"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          {careerAspiration && (
            <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</Button>
          )}
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {careerAspiration ? 'Update growth focus' : 'Save growth focus'}
          </Button>
        </div>
      </div>
    </Card>
  )
}
